import React, { useState } from "react";
import { WizardStep } from "@sscs/types";
import { Dialog, QueryResult, Toast, Wizard } from "@sscs/ui";
import { getQueryStatus } from "@sscs/utils";
import { useGetWhoamiQuery } from "Services";
import { apiErrorToast } from "Utils";
import {
  useGetSharedReportLinkRecipientsQuery,
  useSendInvitesMutation,
} from "../../../services/sharedReports.api";
import {
  SharedReportInfo,
  SharedReportPreviousRecipientsForm,
  SharedReportRecipientsForm,
} from "../../../types";
import {
  DEFAULT_RECIPIENTS_FORM_DATA,
  getRecipientsFormInitialValues,
  getSharedReportPreviousRecipientsFormSchema,
  getSharedReportRecipientsFormSchema,
  updateRecipientsFormWithPreviousRecipients,
} from "../../../utils";
import commonStyles from "../../common.module.scss";
import WizardSteps from "../common/WizardSteps";
import {
  REPORT_LINK_OVERVIEW_WIZARD_LABELS,
  ReportLinkOverviewWizardStep,
} from "./constants";

type ShareReportStepForm =
  | SharedReportPreviousRecipientsForm
  | SharedReportRecipientsForm;

type ManageInvitesDialogProps = {
  sharedReport: SharedReportInfo;
  purl: string;
  onClose: () => void;
  isOpen: boolean;
};

const ManageInvitesDialogContent = ({
  sharedReport,
  purl,
  onClose,
}: Omit<ManageInvitesDialogProps, "isOpen">): React.JSX.Element => {
  const [activeWizardStep, setActiveWizardStep] = useState(
    ReportLinkOverviewWizardStep.PreviousRecipientsStep,
  );
  const [formData, setFormData] = useState<SharedReportRecipientsForm>(
    DEFAULT_RECIPIENTS_FORM_DATA,
  );

  const { data: whoami } = useGetWhoamiQuery();

  const {
    data: existingRecipients,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetSharedReportLinkRecipientsQuery({ token_key: sharedReport.token });
  const queryStatus = getQueryStatus({
    isLoading,
    isFetching,
    isError,
    error,
    data: existingRecipients,
  });

  const [sendReportInvites, { isLoading: isSendingInvites }] =
    useSendInvitesMutation();

  const selectedExistingRecipientEmails =
    formData.recipients !== null && existingRecipients
      ? existingRecipients
          .filter((recipient) => formData.recipients.includes(recipient.email))
          .map((recipient) => recipient.email)
      : [];
  const initialPreviousRecipients =
    existingRecipients?.map((recipient) => ({
      email: recipient.email,
      lastOpened: recipient.last_opened,
      isSelected: selectedExistingRecipientEmails.includes(recipient.email),
    })) ?? [];
  const oldRecipients =
    existingRecipients
      ?.filter((recipient) => formData.recipients?.includes(recipient.email))
      .map((recipient) => recipient.email) ?? [];
  const newRecipients =
    formData.recipients?.filter(
      (recipient) => !oldRecipients.includes(recipient),
    ) ?? [];

  const handleNext = (data?: ShareReportStepForm) => {
    if (data) {
      setFormData({ ...formData, ...data });
    }
    setActiveWizardStep((previous) => previous + 1);
  };

  const handlePrevious = () => {
    setActiveWizardStep((previous) => previous - 1);
  };

  const sendInvites = async () => {
    const recipients = formData.recipients ? [...formData.recipients] : [];

    if (formData.send_copy_to_me && whoami?.email && recipients.length > 0) {
      recipients.push(whoami.email);
    }

    try {
      await sendReportInvites({
        token_key: sharedReport?.token,
        recipients,
        message: recipients.length > 0 ? formData.message : "",
      }).unwrap();

      Toast.show({
        title: "The report has been shared successfully",
        type: "success",
      });
    } catch (error) {
      apiErrorToast(error, "Could not generate link");
      return;
    }
  };

  return (
    <Wizard
      title={
        <>
          Manage invites for the <em>{sharedReport?.settings.label}</em>
        </>
      }
      subtitle={<code>{purl}</code>}
      activeStep={activeWizardStep}
      steps={
        [
          {
            label:
              REPORT_LINK_OVERVIEW_WIZARD_LABELS[
                ReportLinkOverviewWizardStep.PreviousRecipientsStep
              ],
            component: (
              <QueryResult status={queryStatus}>
                <WizardSteps.PreviousRecipientsStep
                  initialPreviousRecipients={initialPreviousRecipients}
                />
              </QueryResult>
            ),
            formValues: {
              recipients: initialPreviousRecipients
                .filter((recipient) => recipient.isSelected)
                .map((recipient) => recipient.email),
            },
            schema: getSharedReportPreviousRecipientsFormSchema(),
            onNext: (data: SharedReportRecipientsForm) => {
              const recipientsForm = updateRecipientsFormWithPreviousRecipients(
                formData,
                (existingRecipients ?? []).map((recipient) => ({
                  email: recipient.email,
                  lastOpened: recipient.last_opened,
                  isSelected:
                    data.recipients?.includes(recipient.email) ?? false,
                })),
              );

              handleNext(recipientsForm);
            },
            onPrevious: handlePrevious,
          },
          {
            label:
              REPORT_LINK_OVERVIEW_WIZARD_LABELS[
                ReportLinkOverviewWizardStep.RecipientsStep
              ],
            component: (
              <QueryResult status={queryStatus}>
                <WizardSteps.RecipientsStep recipientsRequired />
              </QueryResult>
            ),
            formValues: getRecipientsFormInitialValues(
              sharedReport?.settings.label ?? "",
              formData,
            ),
            formMode: "onChange",
            schema: getSharedReportRecipientsFormSchema(true),
            formProps: { dense: true },
            onNext: handleNext,
            onPrevious: handlePrevious,
          },
          {
            label:
              REPORT_LINK_OVERVIEW_WIZARD_LABELS[
                ReportLinkOverviewWizardStep.ReviewStep
              ],
            nextStep: { label: "Send", isLoading: isSendingInvites },
            component: (
              <QueryResult status={queryStatus}>
                <WizardSteps.ReviewStep
                  formData={{
                    ...sharedReport.settings,
                    ...formData,
                  }}
                  resentToEmailAddressesCount={oldRecipients.length}
                  sentToEmailAddressesCount={newRecipients.length}
                />
              </QueryResult>
            ),
            onNext: async () => {
              await sendInvites();
              handleNext();
            },
            onPrevious: handlePrevious,
          },
          {
            label:
              REPORT_LINK_OVERVIEW_WIZARD_LABELS[
                ReportLinkOverviewWizardStep.ShareReportStep
              ],
            nextStep: { label: "Close" },
            previousStep: { isHidden: true },
            component: (
              <QueryResult status={queryStatus}>
                <WizardSteps.ShareReportStep
                  sharedReport={sharedReport}
                  resentToEmailAddressesCount={oldRecipients.length}
                  sentToEmailAddressesCount={newRecipients.length}
                />
              </QueryResult>
            ),
            onNext: onClose,
          },
        ] satisfies readonly [
          WizardStep<SharedReportPreviousRecipientsForm>,
          WizardStep<SharedReportRecipientsForm>,
          WizardStep,
          WizardStep,
        ]
      }
      onStepChange={setActiveWizardStep}
      dialogContentClasses={commonStyles["dialog-content"]}
    />
  );
};

const ManageInvitesDialog = ({
  isOpen,
  ...props
}: ManageInvitesDialogProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={props.onClose} size="md">
    <ManageInvitesDialogContent {...props} />
  </Dialog.Frame>
);

export default ManageInvitesDialog;
